import React from "react";

const MembershipsList = ({ data = [], onHistory, onUpgrade, onCancel }) => {

  const isAmbassadorPlan = (item) => {
    return item.planId?.type?.toUpperCase() === "AMBASSADOR";
  };

  const isProPlan = (item) => {
    return item.planId?.type?.toUpperCase() === "PRO";
  };

  const isActiveSubscription = (item) => {
    return item.status?.toUpperCase() === "ACTIVE";
  };

  const isExpiredSubscription = (item) => {
    return item.status?.toUpperCase() === "EXPIRED" || item.status?.toUpperCase() === "CANCELLED";
  };

  const isUpgraded = (item) => {
    return item.isUpgrade === true || item.source === "ADMIN_UPGRADE";
  };

  const getStatusDisplay = (item) => {
    // ✅ If upgraded, show UPGRADED instead of EXPIRED
    if (isUpgraded(item) && item.status?.toUpperCase() === "EXPIRED") {
      return {
        label: "⬆ Upgraded",
        className: "upgraded",
        icon: "⬆"
      };
    }
    
    // ✅ If upgraded but active, show ACTIVE with upgrade badge
    if (isUpgraded(item) && item.status?.toUpperCase() === "ACTIVE") {
      return {
        label: "🟢 Active",
        className: "active",
        icon: "🟢"
      };
    }

    // Default status
    const statusMap = {
      ACTIVE: { label: "🟢 Active", className: "active" },
      EXPIRED: { label: "🔴 Expired", className: "expired" },
      CANCELLED: { label: "🟡 Cancelled", className: "cancelled" },
      PENDING: { label: "🟠 Pending", className: "pending" },
    };
    
    return statusMap[item.status?.toUpperCase()] || { label: item.status, className: "" };
  };

  return (
    <div className="premium-user-list-wrapper">
      <div className="premium-scroll-container">

        {data?.map((item, index) => {
          const statusDisplay = getStatusDisplay(item);
          const upgraded = isUpgraded(item);

          return (
            <div key={item._id || index} className="premium-table-row">

              {/* USER */}
              <div style={{ width: "20%" }} className="plan-cell">
                <div className="plan-info">
                  <div className="user-box">
                    <img
                      src={item.userId?.profileImageURL || "/default-avatar.png"}
                      alt="user"
                      className="user-avatar"
                    />
                    <div>
                      <div className="plan-title">
                        {item.userId?.name || "Unknown User"}
                      </div>
                      <div className="plan-desc">
                        {item.userId?.email || "No email"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PLAN */}
              <div style={{ width: "18%" }}>
                <div className="plan-title">{item.planId?.title}</div>
                <div className={`plan-type ${item.planId?.type?.toLowerCase()}`}>
                  {item.planId?.type}
                </div>
                {/* ✅ Show upgrade badge */}
                {upgraded && (
                  <div className="upgrade-badge">
                    ⬆ Upgraded
                  </div>
                )}
                {/* ✅ Show previous plan if upgraded */}
                {upgraded && item.previousPlanTitle && (
                  <div className="previous-plan">
                    From: {item.previousPlanTitle}
                  </div>
                )}
              </div>

              {/* STATUS */}
              <div style={{ width: "16%" }}>
                <span className={`status-badge ${statusDisplay.className}`}>
                  {statusDisplay.label}
                </span>
                {/* ✅ Show upgrade date */}
                {upgraded && item.upgradedAt && (
                  <div className="upgrade-date">
                    Upgraded: {new Date(item.upgradedAt).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* DATE */}
              <div style={{ width: "18%" }} className="plan-desc">
                <div>Start: {item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString() : "-"}</div>
                <div style={{ fontSize: "11px", color: "#aaa" }}>
                  Expiry: {
                    item.expiryDate
                      ? new Date(item.expiryDate).toLocaleDateString()
                      : "Lifetime"
                  }
                </div>
              </div>

              {/* ACTIONS */}
              <div style={{ width: "28%", display: "flex", gap: "8px", flexWrap: "wrap" }}>

                {/* View History - Always visible */}
                <button
                  className="history-btn"
                  onClick={() => onHistory(item)}
                  title="View subscription history"
                >
                  📋 History
                </button>

                {/* Upgrade Plan - Only for ACTIVE subscriptions */}
                {isActiveSubscription(item) && (
                  <button
                    className="upgrade-plan-btn"
                    onClick={() => onUpgrade(item)}
                    title="Upgrade user's plan"
                  >
                    ⬆ Upgrade
                  </button>
                )}

                {/* Cancel Access - Only for ACTIVE AMBASSADOR plan */}
                {isAmbassadorPlan(item) && isActiveSubscription(item) && (
                  <button
                    className="cancel-btn"
                    onClick={() => onCancel(item)}
                    title="Cancel ambassador access"
                  >
                    ✖ Cancel
                  </button>
                )}

                {/* PRO Plan Note */}
                {isProPlan(item) && isActiveSubscription(item) && (
                  <span className="pro-cancel-note" title="User can cancel from app">
                    🔒 User cancel
                  </span>
                )}

                {/* Expired/Cancelled note */}
                {isExpiredSubscription(item) && !upgraded && (
                  <span className="expired-note">
                    ⏳ {item.status}
                  </span>
                )}

              </div>

            </div>
          );
        })}
      </div>

      <style>{`
        .premium-table-row {
          display: flex;
          padding: 18px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          align-items: center;
          flex-wrap: wrap;
        }

        .premium-table-row:hover {
          background: rgba(255,255,255,0.02);
        }

        .plan-title {
          color: #fff;
          font-weight: 600;
          font-size: 14px;
        }

        .plan-desc {
          color: #cbd5f5;
          font-size: 13px;
        }

        /* Status Badges */
        .status-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.active {
          background: rgba(34,197,94,0.15);
          color: #22c55e;
        }

        .status-badge.expired {
          background: rgba(239,68,68,0.15);
          color: #ef4444;
        }

        .status-badge.cancelled {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
        }

        .status-badge.pending {
          background: rgba(245,158,11,0.2);
          color: #f59e0b;
        }

        .status-badge.approved {
          background: rgba(34,197,94,0.2);
          color: #22c55e;
        }

        .status-badge.rejected {
          background: rgba(239,68,68,0.2);
          color: #ef4444;
        }

        .status-badge.upgraded {
          background: rgba(59,130,246,0.15);
          color: #3b82f6;
        }

        /* Upgrade Badge */
        .upgrade-badge {
          font-size: 10px;
          color: #3b82f6;
          background: rgba(59,130,246,0.1);
          padding: 2px 8px;
          border-radius: 10px;
          margin-top: 4px;
          display: inline-block;
        }

        .previous-plan {
          font-size: 10px;
          color: rgba(255,255,255,0.3);
          margin-top: 2px;
        }

        .upgrade-date {
          font-size: 10px;
          color: rgba(255,255,255,0.3);
          margin-top: 4px;
        }

        .user-box {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid rgba(255,255,255,0.1);
        }

        .plan-type {
          font-size: 11px;
          margin-top: 4px;
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
        }

        .plan-type.free {
          background: rgba(255,255,255,0.1);
          color: #ccc;
        }

        .plan-type.pro {
          background: rgba(59,130,246,0.2);
          color: #3b82f6;
        }

        .plan-type.ambassador {
          background: rgba(251,191,36,0.2);
          color: #facc15;
        }

        /* Buttons */
        .history-btn {
          background: #3b82f6;
          color: #fff;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.3s;
        }

        .history-btn:hover {
          background: #2563eb;
          transform: scale(1.02);
        }

        .upgrade-plan-btn {
          background: #28a745;
          color: white;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.3s;
        }

        .upgrade-plan-btn:hover {
          background: #218838;
          transform: scale(1.02);
        }

        .cancel-btn {
          background: #f59e0b;
          color: #fff;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.3s;
        }

        .cancel-btn:hover {
          background: #d97706;
          transform: scale(1.02);
        }

        .pro-cancel-note {
          font-size: 11px;
          color: rgba(255,255,255,0.3);
          padding: 6px 10px;
          background: rgba(255,255,255,0.03);
          border-radius: 6px;
          border: 1px dashed rgba(255,255,255,0.06);
          white-space: nowrap;
        }

        .expired-note {
          font-size: 11px;
          color: rgba(255,255,255,0.2);
          padding: 6px 10px;
          background: rgba(255,255,255,0.02);
          border-radius: 6px;
        }

        @media (max-width: 768px) {
          .premium-table-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .premium-table-row > div {
            width: 100% !important;
          }

          .action-btn-group {
            width: 100%;
            flex-wrap: wrap;
          }

          .history-btn,
          .upgrade-plan-btn,
          .cancel-btn {
            flex: 1;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default React.memo(MembershipsList);